import { Box, Button, Grid, MenuItem, Modal, Select, Stack, Switch, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { TagIntensity } from '@/screens/exercises/elements/TagIntensity';
import { Close } from '@mui/icons-material';
import { modalStyle } from '../../../components/modalStyle';
import { BodyZone, MuscleIntensity, DeckDTO } from '../../../generated';
import { Intensity } from '../../../models/intensity';
import { filterBodyzones } from '@/utils/bodyzone-filter';
import { useBodyZoneListQuery, useMuscleTagsQuery } from '@/hooks/query-hooks';

const noZone: BodyZone = { id: 'nz', isDefaultZone: false, name: 'None', zoneTagIds: [] };

type TagEditModalProps = {
  deck?: DeckDTO;
  selectedMuscleIntensities: MuscleIntensity[];
  setSelectedMuscleIntensities: (intensities: MuscleIntensity[]) => void;
  showEditButtons?: boolean;
  show: boolean;
  setShow: (show: boolean) => void;
  selectionComplete?: () => void;
};

export const TagEditModal = (props: TagEditModalProps) => {
  const [filterName, setFilterName] = useState<string>('');
  const [selectedBodyZone, setSelectedBodyZone] = useState<BodyZone>(noZone);
  const [viewGroups, setViewGroups] = useState<boolean>(false);
  const [allMuscleIntensities, setAllMuscleIntensities] = useState<MuscleIntensity[]>([]);
  const { bodyZoneList } = useBodyZoneListQuery();
  const { muscleTags, muscleTagMap } = useMuscleTagsQuery();

  const filteredTags = muscleTags?.filter((mt) => mt.groupMuscleTagIds?.length === 0 || mt.groupMuscleTagIds === undefined) ?? [];
  const muscleTagGroups = muscleTags?.filter((mt) => (mt.groupMuscleTagIds?.length ?? 0) > 0) ?? [];

  useEffect(() => {
    let filteredByName = viewGroups ? muscleTagGroups : filteredTags;

    if (filterName !== '') {
      filteredByName = filteredByName.filter((mt) => mt.name.toLowerCase().includes(filterName.toLowerCase()));
    }

    if (selectedBodyZone.id !== 'nz') filteredByName = filteredByName.filter((f) => selectedBodyZone.zoneTagIds.includes(f.id));

    setAllMuscleIntensities(filteredByName.map((mt) => ({ intensity: Intensity.medium, muscleId: mt.id })));
  }, [filterName, selectedBodyZone, viewGroups]);

  return (
    <Modal
      open={props.show}
      onClose={() => {
        props.setShow(false);
      }}
    >
      <Box sx={{ ...modalStyle, width: '90%' }}>
        <Box sx={{ my: 1 }}>
          <Stack direction={'row'}>
            <Typography>Edit Tags</Typography>
            <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
              <Close onClick={() => props.setShow(false)} />
            </Box>
          </Stack>
        </Box>
        <Box sx={{ my: 1 }}>
          <Stack sx={{ alignContent: 'center', alignItems: 'center', m: 2 }} direction={'row'}>
            <Switch
              checked={viewGroups}
              onChange={() => {
                setViewGroups(!viewGroups);
              }}
            />
            <Typography>Groups</Typography>
          </Stack>
          <TextField sx={{ mx: 1 }} label="Filter" autoFocus={true} value={filterName} onChange={(e: any) => setFilterName(e.target.value)} />
          <Select value={selectedBodyZone.id}>
            {[noZone, ...filterBodyzones(bodyZoneList ?? [], props.deck)].map((bz) => (
              <MenuItem
                key={bz.id}
                value={bz.id}
                selected={selectedBodyZone.id === bz.id}
                onClick={(e) => {
                  setSelectedBodyZone(bz);
                }}
              >
                {bz.name}
              </MenuItem>
            ))}
          </Select>
          <Button
            sx={{ m: 1 }}
            onClick={() => {
              setFilterName('');
              setSelectedBodyZone(noZone);
            }}
          >
            Clear Filter
          </Button>
          <Box sx={{ my: 1 }}>
            {allMuscleIntensities?.map((mi) => {
              const muscle = muscleTagMap.get(mi.muscleId);
              const isGroup = (muscle?.groupMuscleTagIds?.length ?? 0) > 0;

              const selected = props.selectedMuscleIntensities.find((ti) => ti.muscleId === mi.muscleId);

              const muscleIsSelected = isGroup
                ? muscle?.groupMuscleTagIds?.every((gtid) => props.selectedMuscleIntensities.find((mti) => mti.muscleId === gtid))
                : props.selectedMuscleIntensities.find((ti) => ti.muscleId === mi.muscleId) !== undefined;

              return (
                <TagIntensity
                  muscleIntensity={selected ?? mi}
                  editable
                  bgColor={muscleIsSelected ? 'rgb(40, 54, 24)' : undefined}
                  elementClicked={(e, del) => {
                    if (muscleTagMap.get(e.muscleId)?.groupMuscleTagIds?.length) {
                      const muscleTagIds = muscleTagMap.get(e.muscleId)?.groupMuscleTagIds;
                      let result = props.selectedMuscleIntensities.filter((smi) => !muscleTagIds?.includes(smi.muscleId));

                      if (!del && muscleTagIds?.length) result = [...result, ...muscleTagIds?.map((mtid) => ({ muscleId: mtid, intensity: e.intensity }))];
                      props.setSelectedMuscleIntensities(result);
                    } else {
                      let result = props.selectedMuscleIntensities.filter((smi) => smi.muscleId !== e.muscleId);
                      if (!del) result.push(e);
                      props.setSelectedMuscleIntensities(result);
                    }
                  }}
                />
              );
            })}
          </Box>
          <Grid container>
            {props.showEditButtons ? (
              <>
                <Grid>
                  <Button
                    onClick={() => {
                      if (props.selectionComplete) props.selectionComplete();
                      props.setShow(false);
                    }}
                  >
                    Save Selection
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    onClick={() => {
                      props.setShow(false);
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </>
            ) : undefined}

            <Grid sx={{ display: 'flex' }} flexGrow={1} justifyContent={'flex-end'}>
              <Button
                onClick={() => {
                  props.setSelectedMuscleIntensities([]);
                }}
              >
                Clear All
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};
